import { FC, useEffect, useRef, useState } from 'react';
import { Button, Col, Input, InputRef, Row, Space, Typography } from 'antd';
import { TbSearch, TbSlash, TbUsersPlus } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSlashSearchFocus } from '@/hooks/useSlashSearchFocus';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import CreateTeamModal from '@/shared/components/modals/ResourceCreationModals/CreateTeamModal';
import { SetStateMethod, Team } from '@/types';

import TeamCreationDropdown from './components/TeamCreationDropdown';

interface HeaderProps {
    teams: Team[];
    input: string;
    setInput: SetStateMethod<string>;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const Header: FC<HeaderProps> = ({ teams, input, setInput, open, setOpen }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { token } = useAntDesignTheme();
    const createAccess = useWorkspaceResourceCreateAccess('teams', teams?.length ?? 0);
    const inputRef = useRef<InputRef>(null);

    useSlashSearchFocus(inputRef);

    return (
        <>
            <Typography.Title level={4}>Teams</Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Manage all of the teams in with your workspace here.
            </Typography.Paragraph>
            <Row style={{ paddingBottom: '1rem', borderBottom: `1px solid ${token.colorBorder}` }} gutter={[12, 0]}>
                <Col flex='auto'>
                    <Input
                        ref={inputRef}
                        prefix={<TbSearch style={{ color: token.colorTextQuaternary, marginRight: '0.5rem' }} />}
                        placeholder={`Search Teams...`}
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value);
                        }}
                        suffix={<TbSlash size='1rem' style={{ color: token.colorTextQuaternary }} />}
                        style={{ marginBottom: 0, width: '100%' }}
                    />
                </Col>
                <Col flex='none'>
                    <Space.Compact>
                        <Button
                            icon={<TbUsersPlus size='1rem' style={{ verticalAlign: '-0.2rem' }} />}
                            type='primary'
                            disabled={createAccess.disabled}
                            onClick={() => {
                                if (!createAccess.disabled) {
                                    setOpen(true);
                                }
                            }}
                        >
                            New Team
                        </Button>
                        <TeamCreationDropdown teams={teams} />
                    </Space.Compact>
                    <CreateTeamModal open={open} setOpen={setOpen} />
                </Col>
            </Row>
        </>
    );
};

export default Header;
